import React, { FC, useState, useEffect } from 'react'
import { Box, Typography } from '@mui/material'
import { useNavigate, useLocation } from 'react-router-dom'
import { useOktaAuth } from '@okta/okta-react'
import axios from 'axios'
import {
  BaseLayout,
  Module,
  ModuleHeader,
  ModuleBody,
  ButtonContainer,
  CustomButton,
  Loader,
  FactorEnrollmentCard,
} from 'components'
import { FactorEnrollmentState } from 'components/factor-enrollment-card'
import { getAuthClient, routes, api } from 'config'
import { processErrorMsg, storage, constants } from 'utils'
import { FactorType } from 'models/Mfa.d'
import useStyles from './enroll-styles'

interface FactorStates {
  sms: FactorEnrollmentState
  email: FactorEnrollmentState
  totp: FactorEnrollmentState
}

const Enroll: FC = () => {
  const { classes } = useStyles()
  const navigate = useNavigate()
  const location = useLocation()
  const locationState = location.state as {
    requireSmsEnrollment?: boolean
    forceSmsEnrollment?: boolean
    sessionToken?: string
  } | null
  const { oktaAuth } = useOktaAuth()
  const [transaction, setTransaction] = useState<any>({})
  const [showLoader, setShowLoader] = useState(false)
  const [serviceError, setServiceError] = useState('')
  const [activeEnrollment, setActiveEnrollment] = useState<FactorType | null>(
    null,
  )
  const [isAuthenticatedFlow, setIsAuthenticatedFlow] = useState(false)
  const [pendingSessionToken, setPendingSessionToken] = useState<string | null>(
    null,
  )
  const [enrollmentFactorId, setEnrollmentFactorId] = useState<string | null>(
    null,
  )
  const [pendingActivation, setPendingActivation] = useState<any>(null)

  const [factorStates, setFactorStates] = useState<FactorStates>({
    sms: { status: 'not_enrolled' },
    email: { status: 'not_enrolled' },
    totp: { status: 'not_enrolled' },
  })
  const [availableFactorTypes, setAvailableFactorTypes] = useState<string[]>([])
  const [isForcedSmsMode, setIsForcedSmsMode] = useState(false)
  const [emailAutoEnrolled, setEmailAutoEnrolled] = useState(false)

  const getUserEmail = (): string => {
    const storedUser = storage.getUser()
    if (storedUser && storedUser.includes('@')) {
      return storedUser
    }
    try {
      const tokenStorage = JSON.parse(
        localStorage.getItem('okta-token-storage') ?? '{}',
      )
      const idToken = tokenStorage?.idToken?.idToken
      if (idToken) {
        const payload = JSON.parse(atob(idToken.split('.')[1]))
        return payload.email || ''
      }
    } catch {
      return ''
    }
    return ''
  }
  const userEmail = getUserEmail()

  useEffect(() => {
    if (locationState?.forceSmsEnrollment) {
      setIsForcedSmsMode(true)
    }

    const storedSessionToken = localStorage.getItem('pendingSessionToken')
    if (locationState?.requireSmsEnrollment || storedSessionToken) {
      setIsAuthenticatedFlow(true)
      setPendingSessionToken(
        locationState?.sessionToken || storedSessionToken || null,
      )
      setFactorStates({
        sms: { status: 'not_enrolled' },
        email: { status: 'enrolled', email: storage.getUser() || '' },
        totp: { status: 'not_enrolled' },
      })
      setShowLoader(false)
      return
    }

    const authClient = getAuthClient()

    if (authClient.tx.exists()) {
      setShowLoader(true)
      authClient.tx
        .resume()
        .then((response: any) => {
          storage.setUser(response?.user?.profile?.login)

          if (
            response.status === constants.OKTA_TRANSACTION_STATUS.MFA_REQUIRED
          ) {
            navigate(routes.mfa)
            return
          }

          if (
            response.status ===
            constants.OKTA_TRANSACTION_STATUS.MFA_ENROLL_ACTIVATE
          ) {
            response
              .prev()
              .then((prevResponse: any) => {
                setTransaction(prevResponse)
                if (prevResponse.factors?.length > 0) {
                  setAvailableFactorTypes(
                    prevResponse.factors.map((f: any) => f.factorType),
                  )
                }
                initializeFactorStates(prevResponse)
                setShowLoader(false)
              })
              .catch((e: any) => {
                setShowLoader(false)
                setServiceError(
                  processErrorMsg(e, 'Enroll', 'Going back to enrollment'),
                )
              })
            return
          }

          setTransaction(response)
          if (response.factors?.length > 0) {
            setAvailableFactorTypes(
              response.factors.map((f: any) => f.factorType),
            )
          }
          initializeFactorStates(response)
          setShowLoader(false)
        })
        .catch((e: any) => {
          setShowLoader(false)
          setServiceError(processErrorMsg(e, 'Enroll', 'Resuming transaction'))
        })
    } else {
      navigate(routes.login)
    }
  }, [navigate, locationState])

  useEffect(() => {
    if (isForcedSmsMode && factorStates.sms.status === 'not_enrolled') {
      setFactorStates((prev) => ({
        ...prev,
        sms: { status: 'input' },
      }))
      setActiveEnrollment('sms')
    }
  }, [isForcedSmsMode, factorStates.sms.status])

  const initializeFactorStates = (tx: any) => {
    const newStates: FactorStates = {
      sms: { status: 'not_enrolled' },
      email: { status: 'not_enrolled' },
      totp: { status: 'not_enrolled' },
    }

    if (tx.status === constants.OKTA_TRANSACTION_STATUS.MFA_REQUIRED) {
      tx.factors?.forEach((factor: any) => {
        if (factor.factorType === 'sms') {
          newStates.sms = {
            status: 'enrolled',
            phoneNumber: factor.profile?.phoneNumber,
          }
        } else if (factor.factorType === 'email') {
          newStates.email = {
            status: 'enrolled',
            email: factor.profile?.email,
          }
        } else if (factor.factorType === 'token:software:totp') {
          newStates.totp = { status: 'enrolled' }
        }
      })
    }

    if (tx.status === constants.OKTA_TRANSACTION_STATUS.MFA_ENROLL) {
      tx.factors?.forEach((factor: any) => {
        const isActive = factor.status === 'ACTIVE'
        if (factor.factorType === 'sms') {
          if (isActive) {
            newStates.sms = {
              status: 'enrolled',
              phoneNumber: factor.profile?.phoneNumber,
            }
          }
        } else if (factor.factorType === 'email') {
          if (isActive) {
            newStates.email = {
              status: 'enrolled',
              email: factor.profile?.email,
            }
          }
        } else if (factor.factorType === 'token:software:totp') {
          if (isActive) {
            newStates.totp = { status: 'enrolled' }
          }
        }
      })
    }

    setFactorStates(newStates)
  }

  const updateFactorState = (
    factorType: FactorType,
    updates: Partial<FactorEnrollmentState>,
  ) => {
    setFactorStates((prev) => ({
      ...prev,
      [factorType]: { ...prev[factorType], ...updates },
    }))
  }

  const refreshFactorData = async (): Promise<boolean> => {
    try {
      const tokenStorage = localStorage.getItem('okta-token-storage')
      if (!tokenStorage) return false

      const accessToken = JSON.parse(tokenStorage)?.accessToken?.accessToken
      if (!accessToken) return false

      const response = await axios.get(api.getMfaFactors(), {
        headers: { Authorization: `Bearer ${accessToken}` },
      })

      const emailFactor = response.data.factors?.find(
        (f: any) => f.type === 'email',
      )

      if (emailFactor && emailFactor.status === 'ACTIVE') {
        updateFactorState('email', {
          status: 'enrolled',
          email: emailFactor.profile?.email,
        })
        setEmailAutoEnrolled(true)
        return true
      }

      return false
    } catch (error) {
      console.error('Failed to refresh factor data:', error)
      return false
    }
  }

  const handleEnroll = async (
    factorType: FactorType,
    data: { phoneNumber?: string; email?: string },
  ) => {
    setShowLoader(true)
    setActiveEnrollment(factorType)
    updateFactorState(factorType, { error: undefined })

    try {
      if (isAuthenticatedFlow) {
        const accessToken = JSON.parse(
          localStorage.getItem('okta-token-storage') ?? '{}',
        )?.accessToken?.accessToken

        const axiosConfig = accessToken
          ? { headers: { Authorization: `Bearer ${accessToken}` } }
          : undefined

        if (factorType === 'sms') {
          const response = await axios.post(
            api.enrollMfaFactor(),
            {
              factorType: 'sms',
              phoneNumber: data.phoneNumber,
            },
            axiosConfig,
          )

          setEnrollmentFactorId(response.data.factorId)
          updateFactorState('sms', { phoneNumber: data.phoneNumber })
          updateFactorState(factorType, { status: 'verifying' })
          setShowLoader(false)
          return
        }

        if (factorType === 'totp') {
          const response = await axios.post(
            api.enrollMfaFactor(),
            {
              factorType: 'token:software:totp',
            },
            axiosConfig,
          )

          setEnrollmentFactorId(response.data.factorId)
          const activation =
            response.data.activation || response.data._embedded?.activation
          if (activation) {
            updateFactorState('totp', {
              qrCode: activation.qrCodeUrl || activation._links?.qrcode?.href,
              secret: activation.sharedSecret,
              issuer: 'BrightMLS',
            })
          }
          updateFactorState(factorType, { status: 'verifying' })
          setShowLoader(false)
          return
        }

        if (factorType === 'email') {
          const response = await axios.post(
            api.enrollMfaFactor(),
            {
              factorType: 'email',
              email: data.email,
            },
            axiosConfig,
          )

          setEnrollmentFactorId(response.data.factorId)
          updateFactorState('email', { email: data.email })
          updateFactorState(factorType, { status: 'verifying' })
          setShowLoader(false)
          return
        }
      }

      const oktaFactorType =
        factorType === 'totp' ? 'token:software:totp' : factorType

      const isFactorTypeAvailable =
        availableFactorTypes.includes(oktaFactorType) ||
        transaction.factors?.some((f: any) => f.factorType === oktaFactorType)

      if (!isFactorTypeAvailable) {
        throw new Error(
          `${factorType.toUpperCase()} factor not available for enrollment`,
        )
      }

      const factor = transaction.factors?.find(
        (f: any) => f.factorType === oktaFactorType,
      )

      if (!factor) {
        const stateToken = transaction.data?.stateToken
        if (!stateToken) {
          throw new Error(
            'Unable to enroll: session expired. Please log in again.',
          )
        }

        const oktaDomain = process.env.REACT_APP_OKTA_DOMAIN
        let enrollResponse: any

        if (factorType === 'sms') {
          const response = await axios.post(
            `https://${oktaDomain}/api/v1/authn/factors`,
            {
              stateToken,
              factorType: 'sms',
              provider: 'OKTA',
              profile: { phoneNumber: data.phoneNumber },
            },
          )
          enrollResponse = response.data
          updateFactorState('sms', { phoneNumber: data.phoneNumber })
        } else if (factorType === 'email') {
          const response = await axios.post(
            `https://${oktaDomain}/api/v1/authn/factors`,
            {
              stateToken,
              factorType: 'email',
              provider: 'OKTA',
              profile: { email: data.email },
            },
          )
          enrollResponse = response.data
          updateFactorState('email', { email: data.email })
        } else {
          throw new Error(
            `${factorType.toUpperCase()} enrollment not supported for new users.`,
          )
        }

        setPendingActivation({
          isDirectApi: true,
          stateToken: enrollResponse.stateToken,
          factorId: enrollResponse._embedded?.factor?.id,
        })
        updateFactorState(factorType, { status: 'verifying' })
        setShowLoader(false)
        return
      }

      let enrollResponse: any

      if (factorType === 'sms') {
        enrollResponse = await factor.enroll({
          profile: {
            phoneNumber: data.phoneNumber,
            updatePhone: true,
          },
        })
        updateFactorState('sms', { phoneNumber: data.phoneNumber })
      } else if (factorType === 'email') {
        enrollResponse = await factor.enroll({
          profile: {
            email: data.email,
          },
        })
        updateFactorState('email', { email: data.email })
      } else if (factorType === 'totp') {
        enrollResponse = await factor.enroll()
        const activation = enrollResponse.factor?.activation
        if (activation) {
          updateFactorState('totp', {
            qrCode: activation.qrcode?.href,
            secret: activation.sharedSecret,
            issuer: 'BrightMLS',
          })
        }
      }

      setPendingActivation(enrollResponse)
      updateFactorState(factorType, { status: 'verifying' })
      setShowLoader(false)
    } catch (error: any) {
      setShowLoader(false)
      const errorMsg = processErrorMsg(
        error,
        'Enroll',
        `Enrolling ${factorType}`,
      )
      updateFactorState(factorType, { error: errorMsg, status: 'input' })
    }
  }

  const handleVerify = async (factorType: FactorType, code: string) => {
    setShowLoader(true)
    updateFactorState(factorType, { error: undefined })

    try {
      if (isAuthenticatedFlow && enrollmentFactorId) {
        const accessToken = JSON.parse(
          localStorage.getItem('okta-token-storage') ?? '{}',
        )?.accessToken?.accessToken

        const axiosConfig = accessToken
          ? { headers: { Authorization: `Bearer ${accessToken}` } }
          : undefined

        await axios.post(
          api.activateMfaFactor(enrollmentFactorId),
          { passCode: code },
          axiosConfig,
        )

        updateFactorState(factorType, { status: 'enrolled' })

        if (isForcedSmsMode && factorType === 'sms') {
          const emailWasAutoEnrolled = await refreshFactorData()
          if (emailWasAutoEnrolled) {
            setServiceError('')
          }
        }

        setActiveEnrollment(null)
        setEnrollmentFactorId(null)
        setShowLoader(false)
        return
      }

      if (pendingActivation?.isDirectApi) {
        const oktaDomain = process.env.REACT_APP_OKTA_DOMAIN
        const response = await axios.post(
          `https://${oktaDomain}/api/v1/authn/factors/${pendingActivation.factorId}/lifecycle/activate`,
          {
            stateToken: pendingActivation.stateToken,
            passCode: code,
          },
        )

        const activateResponse = response.data
        setPendingActivation(null)
        // Normalize raw Okta API response to match Okta Auth SDK structure
        const normalizedResponse = {
          ...activateResponse,
          factors: activateResponse._embedded?.factors,
          data: { stateToken: activateResponse.stateToken },
        }
        setTransaction(normalizedResponse)

        if (
          activateResponse.status ===
            constants.OKTA_TRANSACTION_STATUS.MFA_ENROLL ||
          activateResponse.status === constants.OKTA_TRANSACTION_STATUS.SUCCESS
        ) {
          updateFactorState(factorType, { status: 'enrolled' })
          setActiveEnrollment(null)

          const factors = activateResponse._embedded?.factors
          if (factors?.length > 0) {
            setAvailableFactorTypes(factors.map((f: any) => f.factorType))
          }

          if (
            activateResponse.status ===
              constants.OKTA_TRANSACTION_STATUS.SUCCESS &&
            activateResponse.sessionToken
          ) {
            oktaAuth.signInWithRedirect({
              sessionToken: activateResponse.sessionToken,
            })
            return
          }

          if (isForcedSmsMode && factorType === 'sms') {
            const emailFactor = activateResponse._embedded?.factors?.find(
              (f: any) => f.factorType === 'email' && f.status === 'ACTIVE',
            )
            if (emailFactor) {
              updateFactorState('email', {
                status: 'enrolled',
                email: emailFactor.profile?.email,
              })
              setEmailAutoEnrolled(true)
            }
          }
        }

        setShowLoader(false)
        return
      }

      const activationTarget = pendingActivation || transaction
      if (
        !activationTarget ||
        typeof activationTarget.activate !== 'function'
      ) {
        throw new Error(
          'No pending enrollment to verify. Please try enrolling again.',
        )
      }

      const activateResponse = await activationTarget.activate({
        passCode: code,
      })
      setPendingActivation(null)
      setTransaction(activateResponse)

      if (
        activateResponse.status ===
          constants.OKTA_TRANSACTION_STATUS.MFA_ENROLL ||
        activateResponse.status === constants.OKTA_TRANSACTION_STATUS.SUCCESS ||
        activateResponse.status ===
          constants.OKTA_TRANSACTION_STATUS.MFA_REQUIRED
      ) {
        updateFactorState(factorType, { status: 'enrolled' })
        setActiveEnrollment(null)

        if (
          activateResponse.status ===
            constants.OKTA_TRANSACTION_STATUS.SUCCESS &&
          activateResponse.sessionToken
        ) {
          oktaAuth.signInWithRedirect({
            sessionToken: activateResponse.sessionToken,
          })
          return
        }

        if (
          activateResponse.status ===
          constants.OKTA_TRANSACTION_STATUS.MFA_ENROLL
        ) {
          if (activateResponse.factors?.length > 0) {
            setAvailableFactorTypes(
              activateResponse.factors.map((f: any) => f.factorType),
            )
          }
          initializeFactorStates(activateResponse)
          updateFactorState(factorType, { status: 'enrolled' })

          if (isForcedSmsMode && factorType === 'sms') {
            await refreshFactorData()
          }
        }
      }

      setShowLoader(false)
    } catch (error: any) {
      setShowLoader(false)
      const errorMsg = processErrorMsg(
        error,
        'Enroll',
        `Verifying ${factorType}`,
      )
      updateFactorState(factorType, { error: errorMsg })
    }
  }

  const handleResend = async (factorType: FactorType): Promise<void> => {
    try {
      if (isAuthenticatedFlow && enrollmentFactorId) {
        const accessToken = JSON.parse(
          localStorage.getItem('okta-token-storage') ?? '{}',
        )?.accessToken?.accessToken

        const axiosConfig = accessToken
          ? { headers: { Authorization: `Bearer ${accessToken}` } }
          : undefined

        await axios.post(api.resendMfaCode(enrollmentFactorId), {}, axiosConfig)
        return
      }

      if (transaction.resend) {
        await transaction.resend(factorType)
      } else {
        await axios.post(
          `https://${process.env.REACT_APP_OKTA_DOMAIN}/api/v1/authn/factors/${transaction.factor?.id}/lifecycle/resend`,
          {
            stateToken: transaction.data?.stateToken,
          },
        )
      }
    } catch (error: any) {
      const errorMsg = processErrorMsg(
        error,
        'Enroll',
        `Resending ${factorType} code`,
      )
      updateFactorState(factorType, { error: errorMsg })
      throw error // Re-throw so the UI can show failure state
    }
  }

  const handleStateChange = (
    factorType: FactorType,
    state: FactorEnrollmentState,
  ) => {
    updateFactorState(factorType, state)
  }

  const handleContinue = () => {
    if (
      factorStates.sms.status === 'enrolled' &&
      factorStates.email.status === 'enrolled'
    ) {
      if (isAuthenticatedFlow && pendingSessionToken) {
        localStorage.removeItem('pendingSessionToken')
        oktaAuth.signInWithRedirect({ sessionToken: pendingSessionToken })
        return
      }

      if (transaction.sessionToken) {
        oktaAuth.signInWithRedirect({ sessionToken: transaction.sessionToken })
        return
      }

      if (transaction.skip) {
        transaction.skip().then((response: any) => {
          if (response.sessionToken) {
            oktaAuth.signInWithRedirect({ sessionToken: response.sessionToken })
          } else {
            setTransaction(response)
          }
        })
      } else {
        navigate(routes.mfa)
      }
    }
  }

  const handleBack = () => {
    navigate(routes.login)
  }

  const isSmsEnrolled = factorStates.sms.status === 'enrolled'
  const isEmailEnrolled = factorStates.email.status === 'enrolled'
  const canContinue = isSmsEnrolled && isEmailEnrolled

  const isFactorDisabled = (factorType: FactorType) => {
    if (!activeEnrollment) return false
    return activeEnrollment !== factorType
  }

  return (
    <BaseLayout maxWidth="40em">
      <Loader visible={showLoader} />
      <Module
        isModuleInitialized={Boolean(transaction.status) || isAuthenticatedFlow}
        serviceError={serviceError}
      >
        <ModuleHeader
          title="Set Up Two-Factor Authentication"
          isNewLayout
          className={classes.title}
        >
          <Typography variant="body1" className={classes.description}>
            {isForcedSmsMode
              ? 'To complete your account setup, please add your phone number for SMS verification.'
              : 'Protect your account with two-factor authentication. SMS and Email are required, and you can optionally add an Authenticator App for additional security.'}
          </Typography>
        </ModuleHeader>

        <ModuleBody isNewLayout>
          <Box sx={{ mb: 3 }}>
            <FactorEnrollmentCard
              factorType="sms"
              isRequired={true}
              state={factorStates.sms}
              onStateChange={(state) => handleStateChange('sms', state)}
              onEnroll={handleEnroll}
              onVerify={handleVerify}
              onResend={handleResend}
              disabled={isFactorDisabled('sms')}
              showLoader={showLoader && activeEnrollment === 'sms'}
            />

            {(!isForcedSmsMode || factorStates.sms.status === 'enrolled') && (
              <FactorEnrollmentCard
                factorType="email"
                isRequired={true}
                state={factorStates.email}
                onStateChange={(state) => handleStateChange('email', state)}
                onEnroll={handleEnroll}
                onVerify={handleVerify}
                onResend={handleResend}
                disabled={isFactorDisabled('email')}
                showLoader={showLoader && activeEnrollment === 'email'}
                userEmail={userEmail}
                locked={factorStates.email.status === 'enrolled'}
              />
            )}

            {!isForcedSmsMode && (
              <FactorEnrollmentCard
                factorType="totp"
                isRequired={false}
                state={factorStates.totp}
                onStateChange={(state) => handleStateChange('totp', state)}
                onEnroll={handleEnroll}
                onVerify={handleVerify}
                disabled={isFactorDisabled('totp')}
                showLoader={showLoader && activeEnrollment === 'totp'}
              />
            )}
          </Box>

          {emailAutoEnrolled && (
            <Box
              sx={{
                mt: 2,
                mb: 2,
                p: 2,
                backgroundColor: '#e8f5e9',
                borderRadius: 1,
                border: '1px solid #4caf50',
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: '#2e7d32', fontWeight: 500 }}
              >
                ✓ Email authentication was automatically added to your account
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: '#2e7d32', fontSize: '0.875rem', mt: 0.5 }}
              >
                You now have both SMS and Email authentication methods enabled.
              </Typography>
            </Box>
          )}

          {!canContinue && (
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              Please set up SMS and Email authentication to continue. You can
              optionally add an Authenticator App for additional security.
            </Typography>
          )}

          <ButtonContainer
            primary={
              <CustomButton
                onClick={handleContinue}
                disabled={!canContinue}
                type="button"
              >
                Continue
              </CustomButton>
            }
            secondary={
              <CustomButton onClick={handleBack} type="button">
                Back
              </CustomButton>
            }
          />
        </ModuleBody>
      </Module>
    </BaseLayout>
  )
}

export default Enroll
